import { useContext, useEffect, useState } from 'react';
import { AppState } from 'react-native';
import { usePathname } from 'expo-router';
import { useNetInfo } from '@react-native-community/netinfo';
import { useQueryClient } from '@tanstack/react-query';
import AchievementToast from './AchievementToast';
import { GuidePresentationContext } from '@/components/Guides/GuideProvider';
import useAchievementStore from '@/data/store/useAchievementStore';
import useAuthStore from '@/data/store/useAuthStore';
import useKioskStore from '@/data/store/useKioskStore';
import useGuideStore from '@/data/store/useGuideStore';
import useConfirmationStore from '@/data/store/useConfirmationStore';
import useLoader from '@/utils/hooks/useLoader';
import { Query } from '@/utils/react-query/Queries';
import OrganizationSetupWatcher from '@/components/Dashboard/OrganizationSetupWatcher';
import { canManageOrganizationSetup } from '@/config/organizationSetup';

export default function AchievementHost() {
    const { data: user } = Query.people.loggedInUser();
    const scope = `${user?.accountID}:${user?.organizationID}`;
    const queue = useAchievementStore(state => state.queue);
    const remove = useAchievementStore(state => state.remove);
    const clearScope = useAchievementStore(state => state.clearScope);
    const emulating = useAuthStore(state => state.isEmulating);
    const kiosk = useKioskStore(state => state.kioskModeEnabled);
    const blockers = useGuideStore(state => state.blockers);
    const confirming = useConfirmationStore(state => state.isOpen);
    const loading = useLoader(state => state.isEnabled);
    const guide = useContext(GuidePresentationContext);
    const { isConnected } = useNetInfo();
    const [foreground, setForeground] = useState(AppState.currentState === 'active');
    const pathname = usePathname();
    const queryClient = useQueryClient();
    useEffect(() => {
        const listener = AppState.addEventListener('change', state => setForeground(state === 'active'));
        return () => listener.remove();
    }, []);
    useEffect(() => () => clearScope(scope), [scope, clearScope]);
    // Existing source mutations do not all know about milestone/setup queries.
    // One bounded invalidation after successful writes catches them anywhere.
    useEffect(() => {
        let timer;
        const unsubscribe = queryClient.getMutationCache().subscribe(event => {
            if (event.type !== 'updated' || event.action?.type !== 'success') return;
            clearTimeout(timer);
            timer = setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['organizationSetup', user?.organizationID] });
                queryClient.invalidateQueries({ queryKey: ['milestones', user?.organizationID] });
            }, 350);
        });
        return () => { unsubscribe(); clearTimeout(timer); };
    }, [queryClient, user?.organizationID]);

    const enabled = !!user?.accountID && !!user?.organizationID && !emulating && !kiosk
        && foreground && isConnected !== false && !pathname.includes('/checkin') && !pathname.includes('/give');
    const blocked = loading || confirming || guide?.active
        || Object.keys(blockers).some(key => key !== 'milestone-celebration');
    const current = queue.find(item => item.scope === scope
        && (item.kind !== 'setup' || canManageOrganizationSetup(user)));
    const finish = () => {
        remove(current.id);
        current.onDismiss?.();
    };
    return <>
        <OrganizationSetupWatcher key={scope} user={user} enabled={enabled} />
        {enabled && !blocked && current && <AchievementToast
            key={current.id}
            {...current}
            onDismiss={finish}
            onAction={current.onAction ? () => { remove(current.id); current.onAction(); } : undefined}
        />}
    </>;
}
